import { useMemo } from 'react';
import { Sort } from 'state/SimInventory/SimInventoryStore';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import {
  HandlersSimsResponse,
  StructsSimRequestProperties,
} from 'types/__generated__/SimInventoryApi';
import postFetcher from '../utils/postFetcher';
import { ApiBaseError } from '../utils/shared';

export interface SimsResultError extends ApiBaseError {}

export const DEFAULT_PAGINATION = 50 as const;

interface UseSimsProps {
  filters: StructsSimRequestProperties;
  sort: Array<Sort>;
  canLoad: boolean;
  onLoadingSlow?: SimsSWRConfiguration['onLoadingSlow'];
}

type SimsSWRConfiguration = SWRConfiguration<HandlersSimsResponse, SimsResultError>;

const useSims = ({ filters, sort, canLoad, onLoadingSlow }: UseSimsProps) => {
  const { pagination: paginationIn, ...rest } = filters ?? {};

  const pagination = paginationIn?.limit ? paginationIn : { limit: DEFAULT_PAGINATION };

  const key =
    canLoad && (pagination?.offset ?? 0) >= 0
      ? {
          url: `${process.env.VITE_BIZ_API_ROOT}/sim-inventory/sims`,
          body: {
            ...rest,
            pagination,
            sort,
          },
        }
      : null;

  const config: SimsSWRConfiguration = useSWRConfig();

  if (onLoadingSlow) {
    config.onLoadingSlow = onLoadingSlow;
  }

  const { data, error, isLoading, mutate, isValidating } = useSWR<
    HandlersSimsResponse,
    SimsResultError
  >(key, postFetcher, config);

  const sims = useMemo(() => data?.sims ?? new Array(pagination.limit), [data, pagination.limit]);

  return {
    sims,
    isLoading,
    error,
    mutate,
    isValidating,
  };
};

export default useSims;
